import { Field } from '../../components/Field';
import { Toggle } from '../../components/Toggle';
import type {
  ChannelForm,
  FieldErrors,
  ManualEmailForm,
  RingcentralForm,
  TwilioForm,
  WebsiteForm,
} from '../../lib/channelForms';

/**
 * The per-channel credential fields for connect (create) and re-credential
 * (manage) forms. Controlled by the caller's ChannelForm state.
 */

interface FieldsProps<F extends ChannelForm> {
  form: F;
  errors: FieldErrors;
  onChange: (next: F) => void;
}

function TextField<F extends ChannelForm>({
  form,
  errors,
  onChange,
  field,
  label,
  hint,
  type = 'text',
  placeholder,
}: FieldsProps<F> & {
  field: keyof F & string;
  label: string;
  hint?: string;
  type?: string;
  placeholder?: string;
}) {
  return (
    <Field label={label} hint={hint} error={errors[field]}>
      <input
        className="input"
        type={type}
        value={form[field] as string}
        placeholder={placeholder}
        onChange={(e) => onChange({ ...form, [field]: e.target.value })}
        aria-invalid={!!errors[field]}
        // Chrome ignores autocomplete="off" on password-type inputs and fills
        // the saved login pair — which lands the account's email in whatever
        // text field sits above the token. 'new-password' is the one value it
        // honours, and 'off' still helps the surrounding text fields.
        autoComplete={type === 'password' ? 'new-password' : 'off'}
        // Nothing here is a person's credential, so a password manager has no
        // business in this form at all (Safari/Firefox honour this hint).
        data-1p-ignore
        data-lpignore="true"
      />
    </Field>
  );
}

function RadioRow({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="radio-row">
      {options.map((option) => (
        <label key={option.value} className={value === option.value ? 'radio-pill active' : 'radio-pill'}>
          <input
            type="radio"
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}

function ManualEmailFields(props: FieldsProps<ManualEmailForm>) {
  const { form, errors, onChange } = props;
  return (
    <>
      <TextField
        {...props}
        field="email"
        label="Mailbox address"
        placeholder="support@yourcompany.com"
      />
      {errors['imapEnabled'] && <p className="field-error">{errors['imapEnabled']}</p>}

      <div className="form-group">
        <Toggle
          checked={form.imapEnabled}
          onChange={(on) => onChange({ ...form, imapEnabled: on })}
          title="Receive email (IMAP)"
          sub="Pull incoming mail into conversations."
        />
        {form.imapEnabled && (
          <div className="form-grid">
            <TextField {...props} field="imapAddress" label="IMAP server" placeholder="imap.gmail.com" />
            <TextField {...props} field="imapPort" label="Port" />
            <TextField {...props} field="imapLogin" label="Login" />
            <TextField {...props} field="imapPassword" label="Password" type="password" hint="Use an app password if the account has 2FA." />
            <Toggle
              checked={form.imapSsl}
              onChange={(on) => onChange({ ...form, imapSsl: on })}
              title="Use SSL"
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <Toggle
          checked={form.smtpEnabled}
          onChange={(on) => onChange({ ...form, smtpEnabled: on })}
          title="Send email (SMTP)"
          sub="Deliver your replies from this address."
        />
        {form.smtpEnabled && (
          <div className="form-grid">
            <TextField {...props} field="smtpAddress" label="SMTP server" placeholder="smtp.gmail.com" />
            <TextField {...props} field="smtpPort" label="Port" />
            <TextField {...props} field="smtpLogin" label="Login" />
            <TextField {...props} field="smtpPassword" label="Password" type="password" />
            <Toggle
              checked={form.smtpStarttls}
              onChange={(on) => onChange({ ...form, smtpStarttls: on })}
              title="Use STARTTLS"
            />
          </div>
        )}
      </div>
    </>
  );
}

function RingcentralFields(props: FieldsProps<RingcentralForm>) {
  const { form, errors, onChange } = props;
  return (
    <>
      <TextField
        {...props}
        field="phoneNumber"
        label="RingCentral number"
        placeholder="+15166132700"
        hint="E.164, country code included. Texts to this number become conversations."
      />
      <Field label="How should we authenticate?" error={errors['auth']}>
        <RadioRow
          options={[
            { value: 'oauth', label: 'Sign in with RingCentral' },
            { value: 'jwt', label: 'Paste JWT credentials' },
          ]}
          value={form.auth}
          onChange={(auth) => onChange({ ...form, auth: auth as RingcentralForm['auth'] })}
        />
      </Field>
      {form.auth === 'jwt' && (
        <div className="form-grid">
          <TextField {...props} field="clientId" label="Client ID" />
          <TextField {...props} field="clientSecret" label="Client secret" type="password" />
          <TextField {...props} field="jwt" label="JWT credential" type="password" hint="From a RingCentral app with JWT auth enabled." />
        </div>
      )}
    </>
  );
}

function TwilioFields(props: FieldsProps<TwilioForm>) {
  const { form, errors, onChange } = props;
  return (
    <>
      <div className="form-grid">
        <TextField {...props} field="accountSid" label="Account SID" placeholder="AC…" />
        <TextField {...props} field="authToken" label="Auth token" type="password" />
      </div>
      <Field label="Medium" error={errors['medium']}>
        <RadioRow
          options={[
            { value: 'sms', label: 'SMS / MMS' },
            { value: 'whatsapp', label: 'WhatsApp' },
          ]}
          value={form.medium}
          onChange={(medium) =>
            onChange({
              ...form,
              medium: medium as TwilioForm['medium'],
              // WhatsApp senders address by number, never by service SID.
              addressKind: medium === 'whatsapp' ? 'phoneNumber' : form.addressKind,
            })
          }
        />
      </Field>
      <Field label="Send from" error={errors['addressKind']}>
        <RadioRow
          options={[
            { value: 'phoneNumber', label: 'A phone number' },
            ...(form.medium === 'sms'
              ? [{ value: 'messagingServiceSid', label: 'A messaging service' }]
              : []),
          ]}
          value={form.addressKind}
          onChange={(addressKind) =>
            onChange({ ...form, addressKind: addressKind as TwilioForm['addressKind'] })
          }
        />
      </Field>
      {form.addressKind === 'phoneNumber' ? (
        <TextField
          {...props}
          field="phoneNumber"
          label="Phone number"
          placeholder="+15551234567"
          hint="E.164, country code included — +1 is not assumed."
        />
      ) : (
        <TextField {...props} field="messagingServiceSid" label="Messaging service SID" placeholder="MG…" />
      )}
      <div className="form-group">
        <Toggle
          checked={form.voiceEnabled}
          onChange={(on) => onChange({ ...form, voiceEnabled: on })}
          title="Enable voice calls"
          sub="Agents can take and place calls from the dashboard."
        />
        {errors['voiceEnabled'] && <p className="field-error">{errors['voiceEnabled']}</p>}
        {form.voiceEnabled && (
          <div className="form-grid">
            <TextField {...props} field="apiKeySid" label="API key SID" placeholder="SK…" />
            <TextField {...props} field="apiKeySecret" label="API key secret" type="password" />
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Connecting is deliberately thin. Everything about how the widget LOOKS —
 * colours, the Home screen, starters, office hours, where it shows — lives on
 * the Chatbot screen's Chat widget section, beside a live preview of it. Asking for
 * a welcome headline in a modal with no preview produced copy nobody could see
 * until it was already on their homepage, and two places to edit it besides.
 *
 * What stays here is the pair that is not appearance: who may embed it, and
 * whose sign-in identifies a visitor.
 */
function WebsiteFields(props: FieldsProps<WebsiteForm>) {
  const { form, errors, onChange } = props;
  return (
    <>
      <Field
        label="Trusted domains (one per line)"
        error={errors['allowedOrigins']}
        hint="Only pages on these origins may show the widget, e.g. https://www.example.com. Leave empty to allow any site."
      >
        <textarea
          className="textarea"
          rows={3}
          value={form.allowedOrigins}
          onChange={(e) => onChange({ ...form, allowedOrigins: e.target.value })}
          aria-invalid={!!errors['allowedOrigins']}
        />
      </Field>
      <details className="field-advanced">
        <summary>Recognise your signed-in customers</summary>
        <TextField
          {...props}
          field="firebaseProjectId"
          label="Firebase project ID (optional)"
          hint="Only if your website's customer sign-in uses a different Firebase project than the platform. Signed-in visitors are then recognised automatically."
        />
      </details>
      <p className="field-hint">
        After connecting you get one script tag to paste into your site. The Chatbot screen's
        Install section walks through putting it on the page, and its Chat widget section styles
        the widget, writes its welcome screen and sets your office hours.
      </p>
    </>
  );
}

export function ChannelFields({
  form,
  errors,
  onChange,
}: {
  form: ChannelForm;
  errors: FieldErrors;
  onChange: (next: ChannelForm) => void;
}) {
  switch (form.kind) {
    case 'oauth-email':
      return (
        <TextField
          form={form}
          errors={errors}
          onChange={onChange}
          field="email"
          label="Mailbox address (optional)"
          placeholder="support@yourcompany.com"
          hint="If set, signing in with any other account is rejected."
        />
      );
    case 'manual-email':
      return <ManualEmailFields form={form} errors={errors} onChange={onChange} />;
    case 'ringcentral':
      return <RingcentralFields form={form} errors={errors} onChange={onChange} />;
    case 'whatsapp':
      return (
        <>
          <TextField form={form} errors={errors} onChange={onChange} field="phoneNumber" label="WhatsApp number" placeholder="+15551234567" />
          <TextField form={form} errors={errors} onChange={onChange} field="phoneNumberId" label="Phone number ID" hint="From Meta Business → WhatsApp → API setup." />
          <TextField form={form} errors={errors} onChange={onChange} field="businessAccountId" label="Business account ID" hint="From the same API setup screen. The webhook is registered against it, and message templates sync from it." />
          <TextField form={form} errors={errors} onChange={onChange} field="apiKey" label="System-user token" type="password" hint="The permanent token from Business Settings → System Users → Generate New Token." />
          {/*
            Collapsed, because the answer is "leave it alone" for every WABA that lives
            under the platform Meta app — which is the normal case, and the only one
            sphere-connect supports at all. Left reachable rather than removed: a WABA
            under the tenant's OWN app is signed with THAT app's secret, and verifying
            such a webhook against the platform secret drops every inbound message.
          */}
          <details className="field-advanced">
            <summary>Using your own Meta app?</summary>
            <TextField form={form} errors={errors} onChange={onChange} field="appSecret" label="App secret (optional)" type="password" hint="Only when this WABA lives under your own Meta app rather than ours — Meta then signs its webhooks with that app's secret. Meta app → Settings → Basic → App secret. Leave blank otherwise." />
          </details>
          <p className="field-hint">
            Connecting registers the callback URL and verify token against this business account
            for you — there is nothing to paste into Meta. If the token cannot do that, the inbox
            settings show both values and a retry.
          </p>
        </>
      );
    case 'telegram':
      return (
        <TextField
          form={form}
          errors={errors}
          onChange={onChange}
          field="botToken"
          label="Bot token"
          type="password"
          hint="Create a bot with @BotFather and paste its token."
        />
      );
    case 'facebook':
      return (
        <>
          <TextField form={form} errors={errors} onChange={onChange} field="pageId" label="Page ID" hint="Meta Business Suite → Settings → Page info, or the Page's About section." />
          <TextField form={form} errors={errors} onChange={onChange} field="pageAccessToken" label="Page access token" type="password" hint="A long-lived Page token from your own Meta app (Graph API Explorer or a system user), with pages_messaging and pages_manage_metadata." />
          <details className="field-advanced">
            <summary>Using your own Meta app?</summary>
            <TextField form={form} errors={errors} onChange={onChange} field="appSecret" label="App secret (optional)" type="password" hint="Only when the token came from your own Meta app rather than ours — Meta then signs the Page's webhooks with that app's secret. Meta app → Settings → Basic → App secret." />
          </details>
          <p className="field-hint">
            Connecting subscribes the Page to Messenger events for you. Under your own app, the inbox
            settings show the callback URL and verify token that app's webhook must carry.
          </p>
        </>
      );
    case 'instagram':
      return (
        <>
          <TextField form={form} errors={errors} onChange={onChange} field="accessToken" label="Access token" type="password" hint="A long-lived token for the professional account from your Meta app's Instagram product (API setup with Instagram login). The account is read from the token." />
          <details className="field-advanced">
            <summary>Using your own Meta app?</summary>
            <TextField form={form} errors={errors} onChange={onChange} field="appSecret" label="Instagram app secret (optional)" type="password" hint="Only when the token came from your own Meta app — Meta then signs the account's webhooks with that app's Instagram app secret." />
          </details>
          <p className="field-hint">
            Connecting subscribes the account to message events for you. A pasted token is refreshed
            automatically before it expires, as long as it is a long-lived one.
          </p>
        </>
      );
    case 'twilio':
      return <TwilioFields form={form} errors={errors} onChange={onChange} />;
    case 'website':
      return <WebsiteFields form={form} errors={errors} onChange={onChange} />;
  }
}
